import type { IBaseComponent, IBaseField } from "../../models/formFields";

/**
 * Utility functions for form components
 */
export const getErrorListElemID = (id: string): string => `${id}_errors`;

export const getDescribedByElemID = (id: string): string => `${id}_describedby`;

export const showValidationErrors = (props: IBaseComponent): boolean => {
    const hasErrors = props.errors && props.errors.length > 0;
    const showErrors =
        props.showErrorMessages || props.showErrorMessages === undefined;
    return (hasErrors ?? false) && (showErrors ?? false);
};

/**
 * Strip input-only props before forwarding useFormField props to <select>.
 * Composite fields (e.g. month/year selects plus a hidden input) cannot
 * spread inputProps wholesale; omitting these keys keeps all aria-* attrs in
 * sync with useFormField without maintaining a manual allowlist.
 */
export const getSelectPropsFromInputProps = <T extends Record<string, unknown>>(
    inputProps: T,
): Omit<
    T,
    | "type"
    | "autoComplete"
    | "placeholder"
    | "defaultValue"
    | "onKeyPress"
    | "onMouseEnter"
    | "onMouseLeave"
    | "onChange"
    | "onFocus"
    | "onBlur"
> => {
    const {
        type: _type,
        autoComplete: _autoComplete,
        placeholder: _placeholder,
        defaultValue: _defaultValue,
        onKeyPress: _onKeyPress,
        onMouseEnter: _onMouseEnter,
        onMouseLeave: _onMouseLeave,
        onChange: _onChange,
        onFocus: _onFocus,
        onBlur: _onBlur,
        ...selectProps
    } = inputProps;
    return selectProps;
};

/**
 * Utility to get HTML input type from field type
 */
export const getHTMLType = (
    type: IBaseField<HTMLElement, string>["type"],
): React.InputHTMLAttributes<HTMLElement>["type"] => {
    switch (type) {
        case "boolean":
            return "checkbox";
        case "integer":
            return "number";
        case "string":
        case "date":
        case "datetime":
            return "text";
        case "choice":
            return;
        default:
            return type;
    }
};
